import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ago } from '../../components/three/ecosystemState';
import { apiGet } from '../../utils/backendApi';
import { LiveSection } from './LiveFrame';
import { EcosystemSection } from '../org/AppsModule';
import { Spark, useVitals, serviceMemory } from '../../components/VitalsSpark';

// Services — what the instance is made of and how each part is doing. The
// kit phones its container list home every five minutes
// (api/usageReporter.js → POST /apps/usage-report {kind:'health'}); the
// plane keeps the last report on the instance doc (monitor.services) and
// shared/appOperate.js servicesSummary() tells real services from one-time
// init jobs and finished Dev Console runners, so '7/10 up' never again
// counts two exited runners as down. Self-heal restarts come from
// instanceHealth/{slug}/remediations through GET /apps/:org/:app/health.

const BADGE = { ok: 'ok', warn: 'warn', down: 'err', off: 'muted' };
const LEVEL_WORD = { ok: 'healthy', warn: 'starting', down: 'down', off: 'stopped' };
const ROLE_LABEL = { edge: 'Edge', app: 'App', data: 'Data', storage: 'Storage', dev: 'Developer tools', ai: 'Local AI', mail: 'Mail', service: 'Service', init: 'One-time job', runner: 'Runner' };
const ROLE_ORDER = ['edge', 'app', 'data', 'storage', 'ai', 'mail', 'dev', 'service'];

const fmtMem = (mb, limit) => (mb == null ? '—' : `${mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`}${limit ? ` of ${limit >= 1024 ? `${(limit / 1024).toFixed(1)} GB` : `${limit} MB`}` : ''}`);
const imageTag = (img) => { const s = String(img || ''); const i = s.lastIndexOf(':'); return i > s.lastIndexOf('/') ? s.slice(i + 1).slice(0, 24) : s.split('/').pop().slice(0, 24); };

export default function ServicesView({ orgId, appId }) {
  const [eco, setEco] = useState(null);
  const [health, setHealth] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    const pull = () => {
      if (document.visibilityState !== 'visible') return;
      apiGet(`/apps/${orgId}/${appId}/ecosystem`).then((d) => { if (alive) { setEco(d); setErr(''); } }).catch((e) => { if (alive) setErr(e.message); });
      apiGet(`/apps/${orgId}/${appId}/health`).then((d) => { if (alive) setHealth(d); }).catch(() => { if (alive) setHealth((h) => h || { remediations: [] }); });
    };
    pull();
    const t = setInterval(pull, 30_000);
    document.addEventListener('visibilitychange', pull);
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', pull); };
  }, [orgId, appId]);

  // Fleet vitals ring for this instance (host agent docker stats, 5-min
  // buckets over 24 h): memory trend + restart/OOM marks per service, and
  // the detector's flags (leak-suspect / mem-pressure / restart-storm / oom).
  const vitals = useVitals(`/apps/${orgId}/vitals?appId=${appId}`);
  const ring = vitals?.instances ? Object.values(vitals.instances)[0] : null;
  const rep = eco?.servicesReport || null;
  const inst = useMemo(() => eco?.instance || {}, [eco]);
  const external = inst.delivery === 'external';
  const restarts = (health?.remediations || []).filter((r) => r.kind !== 'rollback');
  const heal = eco?.heal || null;

  const posture = useMemo(() => {
    if (!eco) return '';
    if (!rep) return `${inst.name || inst.slug || 'This instance'} has not reported its services yet. A hosted App Kit instance phones home every five minutes once it is live; an app hosted elsewhere never does.`;
    const bits = [];
    if (rep.stale) bits.push(`Last heard ${ago(rep.at)} — the report is overdue (expected every ${Math.round(rep.everyMs / 60e3)} min), so these states are the last known, not current`);
    else bits.push(rep.down.length ? `${rep.down.join(', ')} ${rep.down.length === 1 ? 'is' : 'are'} not running` : `All ${rep.total} services up`);
    if (!rep.stale) bits.push(`reported ${ago(rep.at)} · every ${Math.round(rep.everyMs / 60e3)} min`);
    if (rep.restarted?.length) bits.push(`${rep.restarted.join(', ')} restarted 3+ times — worth a look`);
    if (rep.runners?.some((r) => r.level !== 'off')) bits.push(`${rep.runners.filter((r) => r.level !== 'off').length} runner${rep.runners.filter((r) => r.level !== 'off').length === 1 ? '' : 's'} working`);
    if (restarts.length) bits.push(`${restarts.length} self-heal restart${restarts.length === 1 ? '' : 's'} on record`);
    return `${bits.join(' · ')}.`;
  }, [eco, rep, inst, restarts.length]);

  if (err) return <p className="cs-help">Services unavailable: {err}</p>;
  if (!eco) return <p className="cs-help">Loading services…</p>;

  const groups = rep ? ROLE_ORDER.map((role) => ({ role, items: rep.list.filter((s) => (s.role || 'service') === role) })).filter((g) => g.items.length) : [];
  const unknownRole = rep ? rep.list.filter((s) => !ROLE_ORDER.includes(s.role || 'service')) : [];
  if (unknownRole.length) groups.push({ role: 'service', items: unknownRole });

  return (
    <>
      <p className="live-posture">{posture}</p>
      {!rep && (
        <div className="cs-empty">
          {external
            ? 'This app is hosted outside the fleet. The uptime monitor probes it from outside; there is no container list to show.'
            : inst.status && inst.status !== 'live'
              ? `The instance is ${inst.status}. Services appear within five minutes of it going live.`
              : 'No service report yet. If this instance has been live for more than ten minutes, its phone-home may be blocked — check the backend container\'s logs on the Dev Console.'}
        </div>
      )}

      {rep && (
        <LiveSection title={`Services · ${rep.stale ? 'last known' : `${rep.up}/${rep.total} up`}`} meta="the containers that are the app; one row each, what it does and how it is doing">
          <div className="live-scroll">
            <table className="mf-table live-svc-table">
              <thead>
                <tr><th>Service</th><th>What it does</th><th>State</th><th>Up since</th><th>Restarts</th><th>Memory <span className="cs-help" style={{ margin: 0, fontWeight: 400 }}>· 24 h trend, dots = restart / OOM</span></th><th>Image</th></tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <React.Fragment key={g.role}>
                    <tr className="live-svc-group"><td colSpan={7}>{ROLE_LABEL[g.role] || g.role}</td></tr>
                    {g.items.map((s) => <ServiceRow key={s.name} s={s} stale={rep.stale} ring={ring} thresholds={vitals?.thresholds} />)}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </LiveSection>
      )}

      {rep && (rep.init?.length > 0 || rep.runners?.length > 0) && (
        <LiveSection title="Jobs and runners" meta="not part of the running app — they finish and exit, so they never count as down">
          <div className="live-scroll">
            <table className="mf-table live-svc-table">
              <thead>
                <tr><th>Container</th><th>What it is</th><th>State</th><th>Started</th><th>Exit code</th><th>Image</th></tr>
              </thead>
              <tbody>
                {[...(rep.init || []), ...(rep.runners || [])].map((s) => (
                  <tr key={`${s.kind}-${s.name}`}>
                    <td className="live-mono">{s.name}</td>
                    <td className="cs-help" style={{ margin: 0 }}>{s.kind === 'init' ? 'One-time job' : 'Runner'}{s.about ? ` — ${s.about}` : ''}</td>
                    <td><span className={`ap-badge is-${BADGE[s.level] || 'muted'}`}>{s.level === 'off' ? (s.kind === 'init' ? 'done' : 'finished') : s.level === 'ok' ? 'working' : LEVEL_WORD[s.level]}</span></td>
                    <td>{s.startedAt || s.createdAt ? ago(new Date(s.startedAt || s.createdAt).getTime()) : '—'}</td>
                    <td className="live-num">{s.exitCode == null ? '—' : s.exitCode}</td>
                    <td className="live-mono">{imageTag(s.image)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </LiveSection>
      )}

      <LiveSection title="Acted on its own" meta="self-heal: a service that reports down twice in a row is restarted by the fleet">
        {heal && (heal.cooldownUntil || heal.today != null) && (
          <p className="cs-help">{heal.today != null ? `${heal.today} restart${heal.today === 1 ? '' : 's'} today` : ''}{heal.today != null && heal.cooldownUntil ? ' · ' : ''}{heal.cooldownUntil && heal.cooldownUntil > Date.now() ? `cooling down until ${new Date(heal.cooldownUntil).toLocaleTimeString()}` : ''}</p>
        )}
        {restarts.length ? (
          <ul className="live-remed">
            {restarts.map((r) => (
              <li key={r.id}>
                <span className={`ap-badge is-${r.ok ? 'ok' : 'err'}`}>{r.kind === 'dns' ? 'DNS repaired' : `restarted ${r.service}`}</span>
                <span className="cs-help" style={{ margin: 0 }}>{ago(r.ts)} · {r.reason}{!r.ok && r.error ? ` · ${r.error}` : ''}</span>
              </li>
            ))}
          </ul>
        ) : <p className="cs-help">No automatic restarts on record. Rollbacks of a production deploy are listed under <Link to={`/orgs/${orgId}/apps/${appId}/live/health`}>Health</Link>.</p>}
      </LiveSection>

      <LiveSection title="Live picture" meta="the same services on the instance's stage, with the monitor, twin, security and control plane around them">
        <EcosystemSection orgId={orgId} appId={appId} />
      </LiveSection>
    </>
  );
}

const FLAG_WORD = { 'leak-suspect': 'leak?', 'mem-pressure': 'near cap', 'restart-storm': 'restart storm', oom: 'OOM-killed' };

function ServiceRow({ s, stale, ring, thresholds }) {
  const level = stale ? 'off' : s.level;
  const word = stale ? `last: ${LEVEL_WORD[s.level] || s.level}` : (LEVEL_WORD[level] || level);
  const mem = ring ? serviceMemory(ring.rows, s.name) : null;
  const flags = ring ? Object.values(ring.flags || {}).filter((f) => f.svc === s.name) : [];
  const cap = mem?.cap || s.memLimitMb || 0;
  return (
    <tr className={level === 'down' ? 'is-down' : ''}>
      <td className="live-mono">{s.name}</td>
      <td className="cs-help" style={{ margin: 0 }}>{s.about || '—'}</td>
      <td>
        <span className={`ap-badge is-${BADGE[level] || 'muted'}`}>{word}</span>
        {s.status && s.status !== 'running' && <span className="cs-help" style={{ margin: '0 0 0 6px' }}>{s.status}</span>}
      </td>
      <td title={s.startedAt ? new Date(s.startedAt).toLocaleString() : ''}>{s.startedAt ? ago(new Date(s.startedAt).getTime()) : s.createdAt ? `created ${ago(new Date(s.createdAt).getTime())}` : '—'}</td>
      <td className={`live-num ${s.restartCount >= 3 ? 'live-warn' : ''}`}>
        {s.restartCount == null ? '—' : s.restartCount}
        {s.oomKilled ? <span className="vs-flag is-oom" title="the last exit was the kernel OOM killer">OOM</span> : null}
      </td>
      <td className="live-num">
        {fmtMem(s.memMb, s.memLimitMb)}
        {mem && mem.points.length > 1 ? (
          <Spark points={mem.points} marks={mem.marks} max={cap || undefined} warnAt={cap ? (cap * (thresholds?.memPressurePct ?? 85)) / 100 : undefined} unit=" MB" title={`${s.name} memory, last 24 h${cap ? ` (cap ${cap} MB)` : ''}`} />
        ) : null}
        {flags.map((f) => <span key={f.reason} className={`vs-flag is-${f.reason}`} title={f.detail}>{FLAG_WORD[f.reason] || f.reason}</span>)}
      </td>
      <td className="live-mono" title={s.image || ''}>{imageTag(s.image)}</td>
    </tr>
  );
}
